import { assignKeyIds } from "components/collectionsForm/collectionLongForm/widgets";
import Sample, { LabelMap } from "./Sample";
import {
  IConnection,
  IFormProps,
  IProjectProps,
  INode,
  ICollection,
  NodeRequest,
  IProjectConnection,
  IFormSchemaResponse,
  ICreateCollectionResponse,
  ISaveCollectionResponse,
  IProjectReference,
  IProjectErrorsResponse,
  IPstRetryResponse,
} from "./types";
import { JSONSchema7 } from "json-schema";
import { normalizeDefinitions } from "./formManipulation";
import Carta from "@contextualize/carta";
import { config } from "library/api";

export interface NodesList {
  nextPageToken: string;
  nodes: INode[];
}


export default class CartaObjectStorageClient{

  private service;
  constructor(carta: Carta, _config?: Awaited<typeof config>){
      this.service = carta.service.utilize('carta', 'object-storage');
  }

  // V2 Routes
async listNodes(connection: NodeRequest, node: INode): Promise<NodesList> {
  const response = await this.service.post("api/v2/list-nodes", {
      json:{
          ...connection,
          node: node,
      }
  });

  return (await response.json()) as NodesList;
}


// V1 Routes
async listProjects(): Promise<IProjectProps[]> {
  const response = await this.service.post("project-api/v1/list-projects");

  return (await response.json()) as IProjectProps[];
}

async listSchemas(project: IProjectProps): Promise<IFormProps[]> {
  const response = await this.service.post("project-api/v1/list-schemas", {json:project});

  return (await response.json()) as IFormProps[];
}

async listConnections(project: IProjectProps): Promise<IConnection[]> {
  const response = await this.service.post("project-api/v1/list-connections", {json:project});

  return (await response.json()) as IConnection[];
}

async getCollection(project: ICollection): Promise<string> {
  const response = await this.service.post("project-api/v1/get-collection", {json:project});

  let blob = await response.blob();
  blob = blob.slice(0, blob.size, "text/html");
  return URL.createObjectURL(blob);
}

async getFormSchema(project: ICollection): Promise<IFormSchemaResponse> {
  const response = await this.service.post("project-api/v1/form-schema", {json:project});
  const schemaResponse = (await response.json()) as IFormSchemaResponse;

  normalizeDefinitions(schemaResponse.schema as JSONSchema7) as JSONSchema7;

  // This is needed for properly rendering widgets
  assignKeyIds(schemaResponse.schema as JSONSchema7, schemaResponse.key_info);

  schemaResponse.primaryKeys.forEach(pk=>{
    pk.created = new Date(pk.created);
  })

  return schemaResponse
}

async getSamples(_project: IProjectConnection): Promise<LabelMap> {
  const response = await this.service.get(
    `/project-api/v1/samples/${_project.projectId}/${_project.connectionId}`
  );
  return Sample.fromSampleList(await response.json());
}

async getPstErrors(_project: IProjectReference): Promise<IProjectErrorsResponse> {
  const response = await this.service.get(`/project-api/v1/${_project.projectId}/errors`);
  return (await response.json()) as IProjectErrorsResponse;
}

async clearPstError(_project: IProjectReference, errorId: string): Promise<void> {
  await this.service.delete(`/project-api/v1/${_project.projectId}/errors/${errorId}`);
}

async retryPstError(_project: IProjectReference, errorId: string): Promise<IPstRetryResponse> {
  const response = await this.service.post(`/project-api/v1/${_project.projectId}/errors/${errorId}/pst/retry`);
  return (await response.json()) as IPstRetryResponse;
}

async createCollection(payload: {
  projectId: string;
  key: object;
  formData: object;
}): Promise<ICreateCollectionResponse[]> {
  const response = await this.service.post("project-api/v1/create-collection", {json: payload});
  return response.json();
}



async saveCollection(payload: {
  projectId: string;
  connectionId?: string;
  key: object;
  formData: any;
  nativeId: any;
}): Promise<ISaveCollectionResponse[]> {
  const response = await this.service.post("project-api/v1/save-collection", {json: {
    projectId: payload.projectId,
    connectionId: payload.connectionId,
    key: payload.key,
    formData: payload.formData,
    node: {nativeId: payload.nativeId}
  }});
  return response.json();
}
}